import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, ArrowRight, Loader2, CheckCircle2, MapPin, Search, Pencil, Stethoscope, Check, ShieldCheck } from "lucide-react";
import HintDot from "@/components/HintDot";

const DOSAGE_FORMS = ["Tablet", "Capsule", "Solution", "Suspension", "Inhaler", "Cream", "Ointment", "Drops", "Injection", "Compound", "Other"];
const PET_TYPES = ["Dog", "Cat", "Bird", "Reptile", "Horse", "Rabbit", "Other"];


const ZIP_RE = /^\d{5}(-\d{4})?$/;
const POSTAL_RE = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

const initialForm = {
  medication_name: "", strength: "", dosage_form: "Tablet", pack_size: "", quantity: "1", shipping_method: "standard",
  pet_type: "Dog", pet_name: "", pet_dob: "",
  full_name: "", email: "", phone: "", customer_dob: "",
  street: "", apt: "", city: "", state_province: "", postal_code: "", country: "",
};

const DIAL_CODES = [
  { code: "+1",  flag: "🇺🇸", label: "US +1" },
  { code: "+1",  flag: "🇨🇦", label: "CA +1" },
  { code: "+44", flag: "🇬🇧", label: "GB +44" },
  { code: "+61", flag: "🇦🇺", label: "AU +61" },
  { code: "+91", flag: "🇮🇳", label: "IN +91" },
  { code: "+49", flag: "🇩🇪", label: "DE +49" },
  { code: "+33", flag: "🇫🇷", label: "FR +33" },
  { code: "+52", flag: "🇲🇽", label: "MX +52" },
];

export default function EstimateModal({ open, onClose, prefill }) {
  const [form, setForm] = useState(initialForm);
  const [customMed, setCustomMed] = useState("");
  const [manualMode, setManualMode] = useState(false); // user clicked "type manually"
  const [medQuery, setMedQuery] = useState("");
  const [packSizeBase, setPackSizeBase] = useState("");
  const [packOptions, setPackOptions] = useState([]);
  const [packUnit, setPackUnit] = useState("");
  const [shipOptions, setShipOptions] = useState([]);
  const [medResults, setMedResults] = useState([]);
  const [medSearching, setMedSearching] = useState(false);
  const [medMenuOpen, setMedMenuOpen] = useState(false);
  const medSearchTimer = useRef(null);
  const [loading, setLoading] = useState(false);
  const [dialCode, setDialCode] = useState("US +1");
  const [submitted, setSubmitted] = useState(false);
  const { user } = useAuth();
  const isMember = user && user !== false && !user.is_guest;
  const isInsuranceMember = isMember && user.client_type === "insurance_referral";

  useEffect(() => {
    if (!open) {
      setForm(initialForm); setCustomMed(""); setManualMode(false);
      setMedQuery(""); setMedResults([]); setMedMenuOpen(false); setSubmitted(false);
      setPackOptions([]); setPackUnit("");
    }
  }, [open]);

  // The two shipping choices (Standard / Express) with today's FX-converted USD amounts
  useEffect(() => {
    if (!open) return;
    api.get("/shipping/options").then(r => setShipOptions(r.data?.options || [])).catch(() => setShipOptions([]));
  }, [open]);

  // Prefill saved contact + address for signed-in members (only fills empty fields)
  useEffect(() => {
    if (!open || !isMember) return;
    const ap = user.address_parts || {};
    setForm((f) => ({
      ...f,
      full_name: f.full_name || [user.first_name, user.last_name].filter(Boolean).join(" "),
      email: f.email || user.email || "",
      phone: f.phone || user.phone || "",
      customer_dob: f.customer_dob || user.dob || "",
      street: f.street || ap.street || "",
      city: f.city || ap.city || "",
      state_province: f.state_province || ap.state_province || "",
      postal_code: f.postal_code || ap.postal_code || "",
      country: f.country || ap.country || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isMember]);

  // Prefill medication when opened from the hero search (open-estimate-modal event detail)
  useEffect(() => {
    if (!open || !prefill) return;
    if (prefill.med) {
      const r = prefill.med;
      const sizes = Array.isArray(r.pack_sizes) ? r.pack_sizes : [];
      setPackOptions(sizes);
      setPackUnit(r.pack_unit || "");
      setPackSizeBase(r.pack_size || "");
      setManualMode(false);
      setForm((f) => ({ ...f, medication_name: r.name, strength: r.strength || "", dosage_form: r.dosage_form || f.dosage_form, quantity: "1", pack_size: sizes.length === 1 ? packLabel(sizes[0], r.pack_unit || "") : "" }));
      setMedQuery([r.name, r.dosage_form].filter(Boolean).join(" ") + (r.strength ? ` - ${r.strength}` : ""));
      setMedMenuOpen(false);
    } else if (prefill.query) {
      setManualMode(false);
      setMedQuery(prefill.query);
      setMedMenuOpen(true);
    }
  }, [open, prefill]);

  // Lock background scroll when modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Debounced med search → /api/medications/search
  useEffect(() => {
    if (manualMode) return;
    if (medSearchTimer.current) clearTimeout(medSearchTimer.current);
    if (!medQuery || medQuery.trim().length < 2) {
      setMedResults([]); setMedSearching(false);
      return;
    }
    setMedSearching(true);
    medSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/medications/search?q=${encodeURIComponent(medQuery)}&limit=12`);
        setMedResults(data.results || []);
      } catch {
        setMedResults([]);
      } finally {
        setMedSearching(false);
      }
    }, 220);
    return () => { if (medSearchTimer.current) clearTimeout(medSearchTimer.current); };
  }, [medQuery, manualMode]);

  const update = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const validatePostal = () => {
    if (form.postal_code.trim()) return true;
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePostal()) {
    }
    setLoading(true);
    const isCustomMed = manualMode;
    const medName = isCustomMed ? customMed : form.medication_name;
    if (!medName || !medName.trim()) {
      toast.error("Please choose or type a medication");
      setLoading(false);
      return;
    }
    const { street, apt, city, state_province, postal_code, country, ...rest } = form;
    try {
      await api.post("/estimates", {
        ...rest,
        phone: ((DIAL_CODES.find(d => d.label === dialCode)?.code || dialCode) + (form.phone || "")).replace(/\s/g, ""),
        medication_name: medName,
        is_custom_med: isCustomMed,
        qty_is_packs: true,
        shipping_address: { street, apt, city, state_province, postal_code: postal_code.toUpperCase().trim(), country },
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const addressRef = useRef(null);

  // Google Places autocomplete — fires once the modal opens. The Maps script loads
  // async (see public/index.html): if it isn't ready yet, wait for pawlinx:maps-ready.
  // If the key is missing or auth fails, the field stays a normal text input.
  useEffect(() => {
    if (!open) return;
    const input = addressRef.current;
    if (!input) return;
    let listener = null;

    const init = () => {
      if (listener || window.__PAWLINX_MAPS_FAILED) return true;
      if (!window.google?.maps?.places) return false;
      const ac = new window.google.maps.places.Autocomplete(input, {
        types: ["address"],
        fields: ["address_components"],
      });
      listener = ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (!place.address_components) return;
        const get  = t => place.address_components.find(c => c.types.includes(t))?.long_name  ?? "";
        const gets = t => place.address_components.find(c => c.types.includes(t))?.short_name ?? "";
        setForm(prev => ({
          ...prev,
          street:         [get("street_number"), get("route")].filter(Boolean).join(" "),
          city:           get("locality") || get("sublocality") || get("administrative_area_level_2"),
          state_province: gets("administrative_area_level_1"),
          postal_code:    get("postal_code"),
          country:        gets("country"),
        }));
      });
      return true;
    };

    const onReady = () => init();
    if (!init()) window.addEventListener("pawlinx:maps-ready", onReady);
    return () => {
      window.removeEventListener("pawlinx:maps-ready", onReady);
      if (listener) window.google?.maps?.event?.removeListener(listener);
    };
  }, [open]);

  if (!open) return null;

  

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm sm:px-4" data-testid="estimate-modal">
      <div className="bg-white w-full sm:max-w-xl sm:rounded-2xl rounded-t-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 sm:rounded-t-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-paw-primary to-[#0a4fae] px-5 sm:px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-xl bg-white/15 ring-1 ring-white/25 shrink-0">
                <Stethoscope className="w-5 h-5 text-white" />
              </span>
              <div>
                <h2 className="font-outfit font-bold text-base sm:text-lg text-white leading-tight">Get an Instant Estimate</h2>
                <p className="font-manrope text-[12px] text-white/80">See your pet's medication price in minutes</p>
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-white/15 flex items-center justify-center shrink-0" data-testid="estimate-close"><X className="w-4 h-4 text-white" /></button>
          </div>
          <div className="bg-white border-b border-paw-border px-5 sm:px-6 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1">
            {["Free & no commitment", "Vet reviewed", "No payment now"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-paw-text-secondary"><Check className="w-3.5 h-3.5 text-paw-success" /> {t}</span>
            ))}
          </div>
        </div>

        {submitted ? (
          <div className="p-8 sm:p-10 text-center" data-testid="estimate-success">
            <div className="w-16 h-16 rounded-full bg-paw-success/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-paw-success" />
            </div>
            <h3 className="font-outfit font-bold text-xl text-paw-text mb-2">You're all set</h3>
            <p className="font-manrope text-sm text-paw-text-muted max-w-sm mx-auto mb-2">We've emailed you a confirmation. Your personalised quote will follow within minutes. Keep an eye on your inbox.</p>
            <p className="font-manrope text-xs text-paw-warning mb-6">Don't forget to check your <strong>spam / junk folder</strong> if you don't see it!</p>
            <button onClick={onClose} className="bg-paw-primary hover:bg-paw-primary-hover text-white rounded-lg px-6 py-2.5 font-manrope font-semibold text-sm transition-colors">Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5" data-testid="estimate-form">
            {isInsuranceMember && (
              <div className="flex items-start gap-2.5 rounded-xl bg-paw-blue-50 border border-paw-primary/15 px-4 py-3">
                <ShieldCheck className="w-5 h-5 text-paw-primary shrink-0 mt-0.5" />
                <p className="text-[13px] text-paw-navy leading-relaxed"><span className="font-semibold">Covered by your insurance partner.</span> Your details are pre-filled and you won't be charged. Just update anything that's changed.</p>
              </div>
            )}
            {isMember && !isInsuranceMember && (
              <div className="flex items-start gap-2.5 rounded-xl bg-paw-cream border border-paw-border px-4 py-3">
                <CheckCircle2 className="w-5 h-5 text-paw-success shrink-0 mt-0.5" />
                <p className="text-[13px] text-paw-text leading-relaxed">We've pre-filled your saved details. Update anything that's changed.</p>
              </div>
            )}
            {/* Medication */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="grid place-items-center w-5 h-5 rounded-full bg-paw-primary/10 text-paw-primary text-[11px] font-bold">1</span>
                <p className="font-outfit font-semibold text-[11px] uppercase tracking-[0.18em] text-paw-text-muted">Medication</p>
              </div>
              {!manualMode ? (
                <div className="space-y-1.5">
                  <FieldLabel label="Name *" hint={'Search by drug name, strength, or brand (e.g. "trazodone 50mg").'} testid="estimate-med-search" />
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-paw-text-muted pointer-events-none" strokeWidth={1.7} />
                    <input
                      type="text"
                      autoComplete="off"
                      value={medQuery || form.medication_name}
                      onChange={(e) => {
                        setMedQuery(e.target.value);
                        setForm({ ...form, medication_name: "" });
                        setMedMenuOpen(true);
                      }}
                      onFocus={() => setMedMenuOpen(true)}
                      onBlur={() => setTimeout(() => setMedMenuOpen(false), 160)}
                      placeholder="Start typing the medication name…"
                      className="w-full h-11 pl-10 pr-4 rounded-xl border border-paw-border bg-white text-sm font-manrope focus:outline-none focus:border-paw-primary"
                      data-testid="estimate-med-search"
                      required
                    />
                    {medMenuOpen && medQuery.trim().length >= 2 && (
                      <div className="absolute z-20 left-0 right-0 mt-1.5 max-h-64 overflow-y-auto bg-white border border-paw-border rounded-xl shadow-lg" data-testid="estimate-med-results">
                        {medSearching && (
                          <div className="px-4 py-3 text-sm font-manrope text-paw-text-muted flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</div>
                        )}
                        {!medSearching && medResults.length === 0 && (
                          <div className="px-4 py-3 text-sm font-manrope text-paw-text-muted">No matches for "{medQuery}".</div>
                        )}
                        {!medSearching && medResults.map((r) => (
                          <button
                            key={`${r.name}-${r.din}`}
                            type="button"
                            onClick={() => {
                              const sizes = Array.isArray(r.pack_sizes) ? r.pack_sizes : [];
                              setPackOptions(sizes);
                              setPackUnit(r.pack_unit || "");
                              setPackSizeBase(r.pack_size || "");
                              setForm({ ...form, medication_name: r.name, strength: r.strength || "", dosage_form: r.dosage_form || form.dosage_form, quantity: "1", pack_size: sizes.length === 1 ? packLabel(sizes[0], r.pack_unit || "") : "" });
                              setMedQuery([r.name, r.dosage_form].filter(Boolean).join(' ') + (r.strength ? ` - ${r.strength}` : ''));
                              setMedMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-2.5 hover:bg-paw-bg transition-colors flex items-start gap-3 border-b border-paw-border/40 last:border-0"
                            data-testid={`estimate-med-option-${r.din || r.name}`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-manrope text-sm text-paw-text truncate">{[r.name, r.dosage_form].filter(Boolean).join(' ')}{r.strength ? ` - ${r.strength}` : ''}</p>
                              {r.din && <p className="font-manrope text-[11px] text-paw-text-muted">DIN {r.din}</p>}
                            </div>
                          </button>
                        ))}
                        <div className="border-t border-paw-border bg-paw-bg/60 px-4 py-2.5 text-xs font-manrope text-paw-text-muted flex items-center justify-between gap-2">
                          <span>Can't find it?</span>
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { setManualMode(true); setMedMenuOpen(false); setForm({ ...form, medication_name: "" }); }}
                            className="inline-flex items-center gap-1.5 text-paw-accent font-semibold hover:underline"
                            data-testid="estimate-med-manual"
                          >
                            <Pencil className="w-3 h-3" /> Click here to type manually
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <div className="relative flex items-center justify-between h-5">
                    <span className="flex items-center gap-1.5">
                      <label className="font-manrope text-[13px] font-medium leading-5 text-paw-text">Medication name * (manual)</label>
                      <HintDot hint="Our team will personally price this for you and follow up with a quote within 1 business day." testid="estimate-custom-med" />
                    </span>
                    <button type="button" onClick={() => { setManualMode(false); setCustomMed(""); }} className="font-manrope text-[11px] text-paw-primary hover:underline" data-testid="estimate-med-back-search">← Back to search</button>
                  </div>
                  <input
                    value={customMed}
                    onChange={(e) => setCustomMed(e.target.value)}
                    required
                    placeholder="e.g. Apoquel 16mg, we'll price it manually"
                    className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary"
                    data-testid="estimate-custom-med"
                    autoFocus
                  />
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ReadOnlyField label="Strength" value={form.strength} />
                <ReadOnlyField label="Form" value={form.dosage_form} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(!manualMode && packOptions.length > 0) ? (
                  <SelectField
                    label="Pack size *"
                    value={form.pack_size}
                    onChange={(e) => setForm(prev => ({ ...prev, pack_size: e.target.value }))}
                    options={["", ...packOptions.map((ps) => packLabel(ps, packUnit))]}
                    optionLabels={{ "": "Select a pack size" }}
                    required
                    testid="estimate-pack-size"
                    hint="The size of one pack, as it is written on your prescription."
                  />
                ) : (
                  <Field label="Pack size *" placeholder={packSizeBase || "120 Tablets"} value={form.pack_size} onChange={update("pack_size")} required testid="estimate-pack-size" hint="The size of one pack, for example 120 Tablets." />
                )}
                <Field label="Quantity *" type="number" min="1" step="1" placeholder="1" value={form.quantity} onChange={update("quantity")} required testid="estimate-qty" hint="How many packs you need. We dispense up to a three month supply in one order. A licensed pharmacy technician checks your prescription and confirms the final quantity before you pay, so this number can change. If the refills box is blank or says 0, your order defaults to a single fill." />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SelectField label="Pet *" value={form.pet_type} onChange={update("pet_type")} options={PET_TYPES} required testid="estimate-pet-type" />
                <Field label="Pet name" placeholder="Your pet's name" value={form.pet_name} onChange={update("pet_name")} testid="estimate-pet-name" />
              </div>
              {shipOptions.length > 0 && (
                <div className="space-y-1.5">
                  <FieldLabel label="Shipping *" hint="Pick how you would like it delivered. The cost is shown on the estimate we email you." testid="estimate-shipping" />
                  <div className="space-y-2">
                    {shipOptions.map((o) => (
                      <label key={o.method} className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${form.shipping_method === o.method ? "border-paw-primary bg-paw-blue-50" : "border-paw-border hover:border-paw-primary/40"}`}>
                        <input
                          type="radio"
                          name="shipping_method"
                          value={o.method}
                          checked={form.shipping_method === o.method}
                          onChange={() => setForm(prev => ({ ...prev, shipping_method: o.method }))}
                          className="mt-1 accent-[#0a4fae]"
                          data-testid={`estimate-ship-${o.method}`}
                        />
                        <span className="font-manrope text-sm text-paw-text leading-snug">
                          {o.label}{o.days ? <span className="text-paw-text-muted"> ({o.days})</span> : null}{o.note ? <span className="text-paw-text-muted"> · {o.note}</span> : null}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <hr className="border-paw-border" />

            {/* Contact */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="grid place-items-center w-5 h-5 rounded-full bg-paw-primary/10 text-paw-primary text-[11px] font-bold">2</span>
                <p className="font-outfit font-semibold text-[11px] uppercase tracking-[0.18em] text-paw-text-muted">Your details</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Full name *" placeholder="Jane Smith" value={form.full_name} onChange={update("full_name")} required testid="estimate-name" />
                <Field label="Email *" type="email" placeholder="you@email.com" value={form.email} onChange={update("email")} required testid="estimate-email" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 min-w-0">
                <FieldLabel label="Phone" />
                <div className="flex h-11 rounded-xl border border-paw-border overflow-hidden focus-within:border-paw-primary">
                  <select
                    value={dialCode}
                    onChange={e => setDialCode(e.target.value)}
                    className="bg-paw-bg text-sm font-manrope px-2 border-r border-paw-border focus:outline-none shrink-0"
                    data-testid="estimate-phone-dial"
                  >
                    {DIAL_CODES.map(d => (
                      <option key={d.label} value={d.label}>{d.flag} {d.label}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    placeholder="(555) 555-1234"
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="estimate-phone"
                    className="flex-1 px-3 text-sm font-manrope focus:outline-none min-w-0"
                  />
                </div>
              </div>
                <Field label="Date of birth *" type="date" value={form.customer_dob} onChange={update("customer_dob")} required max={new Date().toISOString().slice(0,10)} testid="estimate-dob" hint="A valid date of birth is required. We are not able to process your order without it." />
              </div>
              <div className="flex gap-2.5 items-start bg-paw-primary/5 border border-paw-primary/15 rounded-xl px-3.5 py-2.5" data-testid="estimate-account-notice">
                <span className="w-1.5 h-1.5 mt-2 rounded-full bg-paw-primary flex-shrink-0" />
                <p className="font-manrope text-xs text-paw-text-muted leading-relaxed">
                  We'll create a PawLinx account with this email so you can <strong className="text-paw-text">track your order &amp; view invoices</strong>. You'll get a welcome email with a link to set your password.
                </p>
              </div>
            </section>

            <hr className="border-paw-border" />

            {/* Shipping address */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="grid place-items-center w-5 h-5 rounded-full bg-paw-primary/10 text-paw-primary text-[11px] font-bold">3</span>
                <p className="font-outfit font-semibold text-[11px] uppercase tracking-[0.18em] text-paw-text-muted flex items-center gap-1.5"><MapPin className="w-3 h-3" /> Shipping address</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div data-testid="estimate-street-wrap" className="sm:col-span-2 space-y-1.5 min-w-0"><FieldLabel label="Street address *" /><input ref={addressRef} type="text" value={form.street} onChange={update("street")} placeholder="Start typing your address…" required data-testid="estimate-street" className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" /></div>
                <Field label="Apt / Unit" placeholder="4B" value={form.apt} onChange={update("apt")} testid="estimate-apt" />
              </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="City *" placeholder="Austin" value={form.city} onChange={update("city")} testid="estimate-city" required />
                  <Field label="State / Province" placeholder="TX" value={form.state_province} onChange={update("state_province")} testid="estimate-state" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Postal / ZIP *" placeholder="78701" value={form.postal_code} onChange={update("postal_code")} testid="estimate-postal" required />
                  <Field label="Country" placeholder="US" value={form.country} onChange={update("country")} testid="estimate-country" />
                </div>
            </section>

            <button type="submit" disabled={loading} className="w-full bg-paw-accent hover:bg-paw-accent-hover disabled:opacity-50 text-white rounded-xl h-12 font-manrope font-semibold text-[15px] transition-colors flex items-center justify-center gap-2 shadow-lg shadow-paw-accent/20" data-testid="estimate-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get my instant quote <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="font-manrope text-[11px] text-paw-text-muted text-center pt-1 flex items-center justify-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-paw-success" /> No payment now. Quote arrives by email within minutes.</p>
          </form>
        )}
      </div>
    </div>
  );
}

// Every label in this form renders through here. The row is a fixed 20px tall so a field
// with a hint dot and a field without one still start their input at exactly the same y.
function FieldLabel({ label, hint, testid }) {
  return (
    <div className="relative flex items-center gap-1.5 h-5">
      <label className="font-manrope text-[13px] font-medium leading-5 text-paw-text">{label}</label>
      <HintDot hint={hint} testid={testid} />
    </div>
  );
}

// "120" + "Doses" -> "120 Doses". One place, so the dropdown and the stored value agree.
function packLabel(size, unit) {
  return `${size}${unit ? ` ${unit}` : ""}`;
}

function ReadOnlyField({ label, value, hint, testid }) {
  return (
    <div className="space-y-1.5 min-w-0">
      <FieldLabel label={label} hint={hint} testid={testid} />
      <div className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope bg-paw-bg flex items-center text-paw-text-muted select-none" data-testid={testid}>{value || <span className="opacity-40">Auto-filled</span>}</div>
    </div>
  );
}

function Field({ label, type = "text", testid, hint, ...props }) {
  return (
    <div className="space-y-1.5 min-w-0">
      <FieldLabel label={label} hint={hint} testid={testid} />
      <input type={type} className="w-full h-11 px-3 rounded-xl border border-paw-border text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid={testid} {...props} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, required, testid, optionLabels = {}, hint }) {
  return (
    <div className="space-y-1.5 min-w-0">
      <FieldLabel label={label} hint={hint} testid={testid} />
      <select value={value} onChange={onChange} required={required} className="w-full h-11 px-3 rounded-xl border border-paw-border bg-white text-sm font-manrope focus:outline-none focus:border-paw-primary" data-testid={testid}>
        {options.map(o => <option key={o} value={o}>{optionLabels[o] ?? o}</option>)}
      </select>
    </div>
  );
}
